import { resolveAuthHeaders } from "@/components/api/auth-strategy";
import { navigationEvents, CLOSE_ANIMATION_DELAY, setNavigationPending } from "./navigation-events"

export function reloadPage(): void {
  if (typeof window === "undefined") return
  const { reload } = window.location
  if (typeof reload === "function") reload.call(window.location)
}

/**
 * Fetch a presigned URL from the static endpoint and open it in a new tab.
 *
 * Opens a blank window immediately to preserve the user-gesture context
 * (avoiding pop-up blockers), then fetches the presigned URL and PDF content.
 * If any step fails, the pre-opened window is closed.
 */
export async function openPresignedPdf(staticUrl: string): Promise<void> {
  if (typeof window === "undefined") return;

  // Open window immediately to preserve user-gesture context (avoids pop-up blocker)
  const pdfWindow = window.open("about:blank", "_blank");
  if (!pdfWindow) {
    console.error("Pop-up blocked by browser");
    return;
  }

  try {
    const headers = await resolveAuthHeaders({ url: staticUrl, method: "GET" });

    const response = await fetch(staticUrl, { headers });

    if (!response.ok) {
      console.error(`Failed to fetch presigned URL: status ${response.status}`);
      pdfWindow.close();
      return;
    }

    const data = await response.json();

    if (!data?.presigned_url) {
      console.error("Response missing presigned_url field");
      pdfWindow.close();
      return;
    }

    try {
      const parsed = new URL(data.presigned_url);
      if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
        console.error(`Unsafe presigned URL protocol: ${parsed.protocol}`);
        pdfWindow.close();
        return;
      }
    } catch {
      console.error("Invalid presigned URL format");
      pdfWindow.close();
      return;
    }

    const pdfResponse = await fetch(data.presigned_url);
    if (!pdfResponse.ok) {
      console.error(`Failed to fetch PDF content: status ${pdfResponse.status}`);
      pdfWindow.close();
      return;
    }

    const rawBlob = await pdfResponse.blob();
    const pdfBlob = new Blob([rawBlob], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(pdfBlob);
    pdfWindow.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (error) {
    console.error("Error fetching presigned PDF URL", error);
    pdfWindow.close();
  }
}

export function navigateToUrl(url: string, target: "_blank" | "_self"): void {
  if (typeof window === "undefined") return

  // For _blank, open immediately (doesn't leave the page)
  if (target === "_blank") {
    window.open(url, "_blank", "noopener,noreferrer")
    return
  }

  // For _self navigation, set pending flag and emit close event
  setNavigationPending(true)
  navigationEvents.emit("close-before-navigate", undefined)

  const performNavigation = () => {
    // DEV ONLY: Redirect to iframe preview URL
    if (import.meta.env.DEV) {
      const iframeUrl = `${window.location.origin}${window.location.pathname}?iframe-url=${encodeURIComponent(url)}`
      window.location.href = iframeUrl
      return
    }
    window.location.href = url
  }

  // Wait for close animation before navigating
  setTimeout(performNavigation, CLOSE_ANIMATION_DELAY)
}
